import { Plus, Star, Trash2 } from 'lucide-react';
import { Input } from '@/components/input';
import { Select } from '@/components/select';

export type ChannelRow = { label: string; value: string; is_primary?: boolean };

export const PHONE_LABELS = ['mobile', 'work', 'home', 'main', 'other'];
export const EMAIL_LABELS = ['work', 'personal', 'other'];

/** Add/remove a set of labeled phone or email rows, and mark which one is
 * primary. Shared by the client Contacts tab and the Contacts directory edit
 * dialog. The primary address is what invoices and portal invitations default
 * to, so it has to be settable here rather than inferred from row order. */
export function ChannelFields({
  heading,
  type,
  rows,
  setRows,
  labels,
}: {
  heading: string;
  type: 'phone' | 'email';
  rows: ChannelRow[];
  setRows: (rows: ChannelRow[]) => void;
  labels: string[];
}) {
  function update(index: number, patch: Partial<ChannelRow>) {
    setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function makePrimary(index: number) {
    setRows(rows.map((r, i) => ({ ...r, is_primary: i === index })));
  }

  // Mirrors the server: with nothing marked, the first row is primary.
  const primaryIndex = Math.max(
    rows.findIndex((r) => r.is_primary),
    rows.length > 0 ? 0 : -1,
  );

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {heading}
        </span>
        <button
          type="button"
          onClick={() => setRows([...rows, { label: labels[0], value: '' }])}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          <Plus className="size-3" /> Add
        </button>
      </div>
      {rows.map((row, i) => {
        const isPrimary = i === primaryIndex;
        return (
          <div key={i} className="flex items-center gap-2">
            <Select
              value={row.label}
              onChange={(e) => update(i, { label: e.target.value })}
              className="w-28 shrink-0"
            >
              {labels.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Select>
            <Input
              className="flex-1"
              type={type === 'email' ? 'email' : 'tel'}
              placeholder={type === 'email' ? 'name@example.com' : '(509) 555-0123'}
              value={row.value}
              onChange={(e) => update(i, { value: e.target.value })}
            />
            <button
              type="button"
              onClick={() => makePrimary(i)}
              aria-label={isPrimary ? 'Primary' : 'Make primary'}
              title={
                isPrimary
                  ? `Primary ${type === 'email' ? 'address' : 'number'}`
                  : `Make this the primary ${type === 'email' ? 'address' : 'number'}`
              }
              aria-pressed={isPrimary}
              className={
                isPrimary
                  ? 'shrink-0 text-warning'
                  : 'shrink-0 text-muted-foreground/50 hover:text-warning'
              }
            >
              <Star className={isPrimary ? 'size-4 fill-current' : 'size-4'} />
            </button>
            <button
              type="button"
              onClick={() => setRows(rows.filter((_, j) => j !== i))}
              aria-label="Remove"
              className="shrink-0 text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
